import React, { useMemo, useState } from 'react';
import {
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonList,
  IonInput,
  IonButton,
  IonText,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
} from '@ionic/react';
import { 
  InputColumn, 
  morningstarDataPaths, 
  surveyDataPaths,
  MorningstarColumn,
  NumericalSurveyColumn,
  CategoricalSurveyColumn,
  NumericalSurveyKey,
  RatioColumn,
  DerivedColumn,
} from '../parameters-schema';
import { surveyQuestions } from '../../../components/kyc-editor/question-types';
import { useTags } from '../../tags/tags-provider';
import { useParameters } from '../parameters-provider';

interface ColumnSelectorProps {
  onAddColumn: (column: InputColumn) => void;
  existingColumns: InputColumn[];
  showTagsAndLightStatus?: boolean;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({ 
  onAddColumn, 
  existingColumns,
  showTagsAndLightStatus = false
}) => {
  const { tags } = useTags();
  const { parameters } = useParameters();
  const inferenceRules = parameters?.filter(p => p.type === 'issueFlagger') ?? [];
  const [ratioDisplayName, setRatioDisplayName] = useState('');
  const [ratioNumerator, setRatioNumerator] = useState<string[]>([]);
  const [ratioDenominator, setRatioDenominator] = useState<string[]>([]);

  const ratioOptions = useMemo(() => {
    const numericSurveyOptions = Object.entries(surveyQuestions).filter(
      ([key, question]) =>
        key !== 'retirementYear' &&
        (question.type === 'number' || question.type === 'slider' || question.type === 'choiceNumber')
    );

    const morningstarOptions = Object.entries(morningstarDataPaths).map(([path, label]) => ({
      value: `morningstarData:${path}`,
      label: `Morningstar: ${label}`
    }));

    const surveyOptions = numericSurveyOptions.map(([path, question]) => ({
      value: `surveyAnswers:${path}`,
      label: `KYC: ${question.question || surveyDataPaths[path]}`
    }));

    return [...morningstarOptions, ...surveyOptions];
  }, []);

  const surveyColumnOptions = useMemo(() => {
    const options: { path: string; label: string }[] = [];

    Object.entries(surveyQuestions).forEach(([path, question]) => {
      if (question.type === 'birthdate') {
        options.push({ path: '__derived:age', label: 'Age (Years)' });
        return;
      }

      if (path === 'retirementYear') {
        options.push({ path: '__derived:timeHorizon', label: 'Time Horizon (Years)' });
        return;
      }

      options.push({
        path,
        label: question.question || surveyDataPaths[path]
      });
    });

    return options;
  }, []);

  const ratioOptionMap = useMemo(() => {
    return new Map(ratioOptions.map(option => [option.value, option.label]));
  }, [ratioOptions]);

  const handleAddMorningstarColumn = (path: string) => {
    const column: MorningstarColumn = {
      type: 'numerical',
      source: 'morningstarData',
      path: path as keyof typeof morningstarDataPaths,
      displayName: morningstarDataPaths[path],
    };
    onAddColumn(column);
  };

  const handleAddSurveyColumn = (path: string) => {
    if (path === '__derived:age') {
      handleAddDerivedColumn('age');
      return;
    }

    if (path === '__derived:timeHorizon') {
      handleAddDerivedColumn('timeHorizon');
      return;
    }

    const questionType = surveyQuestions[path as keyof typeof surveyQuestions].type;
    
    if (questionType === 'birthdate') {
      return;
    }

    if (questionType === 'number' || questionType === 'slider' || questionType === 'choiceNumber') {
      const column: NumericalSurveyColumn = {
        type: 'numerical',
        source: 'surveyAnswers',
        path: path as NumericalSurveyColumn['path'],
        displayName: surveyDataPaths[path],
      };
      onAddColumn(column);
    } else if (questionType === 'date') {
      // For date questions, default to numerical with age transformation for DOB
      if (path === 'dob') {
        const column: NumericalSurveyColumn = {
          type: 'numerical',
          source: 'surveyAnswers',
          path: path as NumericalSurveyColumn['path'],
          displayName: `${surveyDataPaths[path]} (Age in Years)`,
        };
        onAddColumn(column);
      } else {
        // For other date questions, treat as categorical
        const column: CategoricalSurveyColumn = {
          type: 'categorical',
          source: 'surveyAnswers',
          path: path as CategoricalSurveyColumn['path'],
          displayName: surveyDataPaths[path],
        };
        onAddColumn(column);
      }
    } else {
      const column: CategoricalSurveyColumn = {
        type: 'categorical',
        source: 'surveyAnswers',
        path: path as CategoricalSurveyColumn['path'],
        displayName: surveyDataPaths[path],
      };
      onAddColumn(column);
    }
  };

  const handleAddDerivedColumn = (key: DerivedColumn['key']) => {
    const label = key === 'age' ? 'Age (Years)' : 'Time Horizon (Years)';
    const column: DerivedColumn = {
      type: 'derived',
      key,
      displayName: label,
    };
    onAddColumn(column);
  };

  const handleAddLightStatusColumn = (parameterName: string) => {
    const column: InputColumn = {
      type: 'lightStatus',
      parameterName,
    };
    onAddColumn(column);
  };

  const handleAddTagColumn = (tagID: string) => {
    const column: InputColumn = {
      type: 'tags',
      tagID,
    };
    onAddColumn(column);
  };

  const parseRatioRef = (value: string): RatioColumn['numerator'][number] | null => {
    const separatorIndex = value.indexOf(':');
    if (separatorIndex === -1) return null;

    const source = value.slice(0, separatorIndex);
    const path = value.slice(separatorIndex + 1);

    if (source === 'morningstarData') {
      return { source: 'morningstarData', path: path as keyof typeof morningstarDataPaths };
    }
    if (source === 'surveyAnswers') {
      return { source: 'surveyAnswers', path: path as NumericalSurveyKey };
    }
    return null;
  };

  const buildRatioLabel = (values: string[]): string => {
    return values.map(value => ratioOptionMap.get(value) || value).join(' + ');
  };

  const handleAddRatioColumn = () => {
    if (!ratioNumerator.length || !ratioDenominator.length) return;

    const numeratorRefs = ratioNumerator
      .map(parseRatioRef)
      .filter((ref): ref is RatioColumn['numerator'][number] => ref !== null);
    const denominatorRefs = ratioDenominator
      .map(parseRatioRef)
      .filter((ref): ref is RatioColumn['denominator'][number] => ref !== null);

    if (!numeratorRefs.length || !denominatorRefs.length) return;

    const defaultLabel = `${buildRatioLabel(ratioNumerator)} / ${buildRatioLabel(ratioDenominator)}`;
    const column: RatioColumn = {
      type: 'ratio',
      displayName: ratioDisplayName.trim() || defaultLabel,
      numerator: numeratorRefs,
      denominator: denominatorRefs,
      displayAsPercent: true
    };

    onAddColumn(column);
    setRatioDisplayName('');
    setRatioNumerator([]);
    setRatioDenominator([]);
  };

  return (
    <div className="column-selector">
      <IonList lines="full">
        <IonItem>
          <IonLabel>Add Morningstar Data Column</IonLabel>
          <IonSelect 
            interface="popover"
            interfaceOptions={{ cssClass: 'column-selector-popover' }}
            placeholder="Select data"
            onIonChange={e => handleAddMorningstarColumn(e.detail.value)}
          >
            {Object.entries(morningstarDataPaths).map(([path, displayName]) => (
              <IonSelectOption key={path} value={path}>
                {displayName}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        <IonItem>
          <IonLabel>Add Survey Data Column</IonLabel>
          <IonSelect 
            interface="popover"
            interfaceOptions={{ cssClass: 'column-selector-popover' }}
            placeholder="Select question"
            onIonChange={e => handleAddSurveyColumn(e.detail.value)}
          >
            {surveyColumnOptions.map(({ path, label }) => (
              <IonSelectOption key={path} value={path}>
                {label}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        {showTagsAndLightStatus && (
          <>
            <IonItem>
              <IonLabel>Add Light Status Check</IonLabel>
              <IonSelect 
                interface="popover"
                interfaceOptions={{ cssClass: 'column-selector-popover' }}
                placeholder="Select inference rule"
                onIonChange={e => handleAddLightStatusColumn(e.detail.value)}
              >
                {inferenceRules.map(rule => (
                  <IonSelectOption key={rule.id} value={rule.id}>
                    {rule.displayTitle || rule.id}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel>Add Tag Check</IonLabel>
              <IonSelect 
                interface="popover"
                interfaceOptions={{ cssClass: 'column-selector-popover' }}
                placeholder="Select tag"
                onIonChange={e => handleAddTagColumn(e.detail.value)}
              >
                {tags?.filter(tag => tag.isProduct !== true).map(tag => (
                  <IonSelectOption key={tag.id} value={tag.id}>
                    {tag.displayName || tag.id}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
          </>
        )}
      </IonList>

      <IonCard className="ratio-group">
        <IonCardHeader>
          <IonCardTitle>Ratio Column</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonItem>
            <IonLabel position="stacked">Ratio Column Label</IonLabel>
            <IonInput
              value={ratioDisplayName}
              onIonChange={e => setRatioDisplayName(e.detail.value || '')}
            />
          </IonItem>
          <div className="ratio-label-helper">
            <IonText color="medium">
              Optional label (auto-generated if blank)
            </IonText>
          </div>

          <IonItem>
            <IonLabel>Ratio Numerator (Sum)</IonLabel>
            <IonSelect
              multiple
              interface="popover"
              interfaceOptions={{ cssClass: 'column-selector-popover' }}
              placeholder="Select inputs"
              value={ratioNumerator}
              onIonChange={e => setRatioNumerator(e.detail.value || [])}
            >
              {ratioOptions.map(option => (
                <IonSelectOption key={option.value} value={option.value}>
                  {option.label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel>Ratio Denominator (Sum)</IonLabel>
            <IonSelect
              multiple
              interface="popover"
              interfaceOptions={{ cssClass: 'column-selector-popover' }}
              placeholder="Select inputs"
              value={ratioDenominator}
              onIonChange={e => setRatioDenominator(e.detail.value || [])}
            >
              {ratioOptions.map(option => (
                <IonSelectOption key={option.value} value={option.value}>
                  {option.label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <div className="ratio-helper">
            <IonText color="medium">
              If the denominator is 0 or missing, the default branch is used.
            </IonText>
          </div>

          <IonButton
            expand="block"
            onClick={handleAddRatioColumn}
            disabled={!ratioNumerator.length || !ratioDenominator.length}
          >
            Add Ratio Column
          </IonButton>
        </IonCardContent>
      </IonCard>

      <style>{`
        .column-selector {
          background: var(--ion-background-color);
          border: 1px solid var(--ion-border-color);
          border-radius: var(--ion-border-radius);
        }

        .column-selector ion-list {
          background: transparent;
        }

        .column-selector ion-item {
          --background: transparent;
        }

        .column-selector ion-select {
          max-width: 100%;
        }

        .column-selector ion-select::part(text) {
          white-space: normal;
          overflow: visible;
        }

        .ratio-group {
          margin: 1rem;
          border: 1px solid var(--ion-border-color);
        }

        .ratio-group ion-card-header {
          padding-bottom: 0;
        }

        .ratio-group ion-card-title {
          font-size: 1rem;
        }

        .ratio-group ion-card-content {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .ratio-group ion-item {
          --background: transparent;
        }

        .ratio-group ion-select {
          max-width: 100%;
        }

        .ratio-helper {
          padding: 0 0.25rem;
        }

        .ratio-label-helper {
          margin: -0.25rem 0 0.5rem 0.25rem;
          font-size: 0.75rem;
        }

        .column-selector-popover {
          --width: min(500px, 95vw);
        }

        .column-selector-popover ion-item,
        .column-selector-popover ion-label {
          white-space: normal !important;
          overflow: visible !important;
        }
      `}</style>
    </div>
  );
};

export default ColumnSelector;
